import dayjs from 'dayjs'

import { StockOverall } from '@/types/stock'
import { fiatCurrencyFormat, formatMarketCap } from '@/utils/number/format'

/**
 * Map of placeholder tokens to their corresponding value getter functions
 * Usage in CMS: Include placeholders like {$MARKET_CAP} in rich text content
 */
export const STOCK_PLACEHOLDER_MAP: Record<string, (stock: StockOverall) => string> = {
  '{$STOCK}': stock => stock.name,
  '{$COMPANY}': stock => stock.name,
  '{$TICKER}': stock => stock.symbol,
  '{$AS_OF_TIMESTAMP_ET}': () => dayjs().format('YYYY-MM-DD'),
  '{$AS_OF_DATE}': () => dayjs().format('YYYY-MM-DD'),
  '{$DELAY_MINUTES}': () => '15',
  '{$MARKET_CAP}': stock => `$${formatMarketCap(stock.usd_marketcap)}`,
  '{$52W_HIGH}': stock => `$${fiatCurrencyFormat(stock.usd_52_week_high)}`,
  '{$52W_LOW}': stock => `$${fiatCurrencyFormat(stock.usd_52_week_low)}`,
  '{$PRICE_USD}': stock => `$${fiatCurrencyFormat(stock.usd_price)}`,
  '{$DIVIDEND_YIELD_%}': stock =>
    stock.usd_dividend_yield ? `${fiatCurrencyFormat(stock.usd_dividend_yield)}%` : 'N/A',
  '{$ANNUAL_DIVIDEND}': stock => `$${fiatCurrencyFormat(stock.usd_dividend_per_share * 12)}`,
  '{$DATA_PROVIDER}': () => 'ICE',
}

/**
 * Creates pattern like: \{\$STOCK\}|\{\$COMPANY\}|...
 * Sorted by length (longest first) to prevent partial matches
 */
const PLACEHOLDER_REGEX = new RegExp(
  Object.keys(STOCK_PLACEHOLDER_MAP)
    .sort((a, b) => b.length - a.length)
    .map(key => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'g',
)

/**
 * Replace all stock placeholder tokens in a text string with actual values
 *
 * @param text - The text containing placeholder tokens
 * @param stock - The stock data object
 * @returns Text with all placeholders replaced with actual values
 */
export function replaceStockPlaceholders(text: string, stock: StockOverall): string {
  if (!text || typeof text !== 'string') return text

  const result = text.replace(PLACEHOLDER_REGEX, match => {
    const getValue = STOCK_PLACEHOLDER_MAP[match]
    if (!getValue) {
      return match
    }

    try {
      const value = getValue(stock)
      return value
    } catch (error) {
      return match
    }
  })

  return result
}
